import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { Mail, KeyRound, ArrowLeft, CheckCircle2, Loader2, Eye, EyeOff } from 'lucide-react';
import { LogoFull } from '../components/brand';

export function ForgotPasswordPage() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setSubmitted(true);
    } catch {
      // Same message either way (anti-enumeration)
      setSubmitted(true);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-6"><LogoFull size="md" /></div>
        <Card className="border border-border shadow-sm rounded-2xl">
          <CardContent className="p-6 md:p-8">
            {submitted ? (
              <div className="text-center space-y-3">
                <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
                <h2 className="text-xl font-bold">Check Your Email</h2>
                <p className="text-sm text-muted-foreground">
                  If an account exists for <strong>{email}</strong>, we've sent a password-reset link.
                  The link expires in 1 hour.
                </p>
                <p className="text-xs text-muted-foreground">
                  Don't see it? Check your spam folder, or contact your program coordinator.
                </p>
                <Link to="/login" className="inline-flex items-center gap-1 text-sm text-teal-600 hover:underline mt-3">
                  <ArrowLeft size={14} /> Back to Sign In
                </Link>
              </div>
            ) : (
              <>
                <div className="flex justify-center mb-4">
                  <div className="w-14 h-14 rounded-full bg-teal-50 flex items-center justify-center">
                    <Mail className="text-teal-600" size={26} />
                  </div>
                </div>
                <h2 className="text-xl font-bold text-center mb-1">Reset Your Password</h2>
                <p className="text-sm text-muted-foreground text-center mb-5">Enter your email and we'll send you a reset link.</p>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium">Email</Label>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      required
                      className="h-11"
                      data-testid="forgot-email-input"
                    />
                  </div>
                  <Button type="submit" disabled={loading} className="w-full h-11 bg-teal-600 hover:bg-teal-700 text-white rounded-full" data-testid="forgot-submit-button">
                    {loading ? <Loader2 size={16} className="animate-spin" /> : 'Send Reset Link'}
                  </Button>
                </form>
                <p className="text-center text-sm text-muted-foreground mt-4">
                  <Link to="/login" className="inline-flex items-center gap-1 text-teal-600 hover:underline">
                    <ArrowLeft size={14} /> Back to Sign In
                  </Link>
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export function ResetPasswordPage() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return toast.error('Passwords do not match');
    if (pw.length < 10) return toast.error('Password must be at least 10 characters');
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: pw });
      toast.success('Password reset! Please sign in.');
      navigate('/login');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    }
    setLoading(false);
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-background">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            <p className="text-sm">This reset link is invalid. Please request a new one.</p>
            <Link to="/forgot-password" className="text-sm text-teal-600 hover:underline mt-3 inline-block">Request New Link</Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-6"><LogoFull size="md" /></div>
        <Card className="border border-border shadow-sm rounded-2xl">
          <CardContent className="p-6 md:p-8">
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 rounded-full bg-teal-50 flex items-center justify-center">
                <KeyRound className="text-teal-600" size={26} />
              </div>
            </div>
            <h2 className="text-xl font-bold text-center mb-1">Set a New Password</h2>
            <p className="text-sm text-muted-foreground text-center mb-5">Minimum 10 characters.</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">New Password</Label>
                <div className="relative">
                  <Input
                    type={showPw ? 'text' : 'password'}
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    placeholder="At least 10 characters"
                    required
                    className="h-11 pr-10"
                    data-testid="reset-new-password-input"
                  />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Confirm Password</Label>
                <Input
                  type={showPw ? 'text' : 'password'}
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  placeholder="Type it again"
                  required
                  className="h-11"
                  data-testid="reset-confirm-password-input"
                />
              </div>
              <Button type="submit" disabled={loading} className="w-full h-11 bg-teal-600 hover:bg-teal-700 text-white rounded-full" data-testid="reset-submit-button">
                {loading ? <Loader2 size={16} className="animate-spin" /> : 'Reset Password'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
